import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { API } from '../../App';
import { toast } from 'sonner';
import {
  Building2,
  Users,
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Key,
  Power,
  Loader2,
  BarChart3,
  CheckCircle,
  XCircle,
  Calendar,
  Mail,
  Phone,
  Copy,
  Eye,
  EyeOff,
  UserPlus,
  Shield,
  User,
  Download,
  Upload,
  Database
} from 'lucide-react';

// Stats Card Component
const StatsCard = ({ title, value, icon: Icon, color, bgColor }) => (
  <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <p className="text-2xl font-bold text-gray-800 mt-1">{value}</p>
      </div>
      <div className={`w-12 h-12 rounded-xl ${bgColor} flex items-center justify-center`}>
        <Icon className={`w-6 h-6 ${color}`} />
      </div>
    </div>
  </div>
);

// Admin Dashboard Main
const AdminDashboard = () => {
  const { user, token, isSuperAdmin } = useAuth();
  const location = useLocation();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">لوحة التحكم</h1>
          <p className="text-gray-500 mt-1">
            {isSuperAdmin ? 'إدارة الشركات والمستخدمين' : 'إدارة موظفي الشركة'}
          </p>
        </div>
      </div>

      {/* Routes */}
      <Routes>
        <Route index element={isSuperAdmin ? <SuperAdminDashboard token={token} /> : <CompanyAdminDashboard token={token} user={user} />} />
        <Route path="companies" element={<CompaniesManagement token={token} />} />
        <Route path="users" element={<UsersManagement token={token} user={user} isSuperAdmin={isSuperAdmin} />} />
      </Routes>
    </div>
  );
};

// Super Admin Dashboard
const SuperAdminDashboard = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="إجمالي الشركات"
          value={stats?.total_companies || 0}
          icon={Building2}
          color="text-blue-600"
          bgColor="bg-blue-50"
        />
        <StatsCard
          title="الشركات النشطة"
          value={stats?.active_companies || 0}
          icon={CheckCircle}
          color="text-green-600"
          bgColor="bg-green-50"
        />
        <StatsCard
          title="إجمالي المستخدمين"
          value={stats?.total_users || 0}
          icon={Users}
          color="text-purple-600"
          bgColor="bg-purple-50"
        />
        <StatsCard
          title="المستخدمين النشطين"
          value={stats?.active_users || 0}
          icon={User}
          color="text-amber-600"
          bgColor="bg-amber-50"
        />
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link
          to="/admin/companies"
          className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-all group"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
              <Building2 className="w-7 h-7 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">إدارة الشركات</h3>
              <p className="text-sm text-gray-500">إضافة وتعديل وحذف الشركات</p>
            </div>
          </div>
        </Link>

        <Link
          to="/admin/users"
          className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-all group"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-purple-50 flex items-center justify-center group-hover:bg-purple-100 transition-colors">
              <Users className="w-7 h-7 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">إدارة المستخدمين</h3>
              <p className="text-sm text-gray-500">إدارة جميع المستخدمين</p>
            </div>
          </div>
        </Link>
      </div>

      {/* Full Database Backup Section */}
      <FullBackupCard token={token} />
    </div>
  );
};

// Full Database Backup Component
const FullBackupCard = ({ token }) => {
  const [loading, setLoading] = useState(false);

  const handleFullBackup = async () => {
    if (!window.confirm('سيتم تنزيل نسخة احتياطية كاملة لجميع البيانات. هل تريد المتابعة؟')) {
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/full-backup`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        toast.error('فشل في إنشاء النسخة الاحتياطية');
        return;
      }
      const data = await response.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').split('T');
      a.download = `full_backup_${ts[0]}_${ts[1].slice(0, 8)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`تم تنزيل النسخة الاحتياطية (${data.total_documents} وثيقة من ${data.total_collections} مجموعة)`);
    } catch (e) {
      toast.error('فشل في إنشاء النسخة الاحتياطية');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200 shadow-sm">
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
          <Database className="w-7 h-7 text-amber-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            نسخة احتياطية كاملة
            <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">موصى به قبل إعادة النشر</span>
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            تنزيل ملف JSON يحتوي على كل البيانات (الموردين، المشاريع، الكشوف، الفواتير، المستخدمين، الشركات...) — يمكنك استخدامه لاسترجاع البيانات لاحقاً.
          </p>
          <button
            data-testid="full-backup-btn"
            onClick={handleFullBackup}
            disabled={loading}
            className="mt-3 inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            تنزيل نسخة احتياطية كاملة
          </button>
        </div>
      </div>
    </div>
  );
};

// Company Admin Dashboard
const CompanyAdminDashboard = ({ token, user }) => {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">معلومات الشركة</h2>
        <p className="text-gray-600">الشركة: {user?.company_name || 'غير محدد'}</p>
      </div>

      <Link
        to="/admin/users"
        className="block bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-all group"
      >
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-purple-50 flex items-center justify-center group-hover:bg-purple-100 transition-colors">
            <Users className="w-7 h-7 text-purple-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">إدارة الموظفين</h3>
            <p className="text-sm text-gray-500">إضافة وإدارة موظفي الشركة</p>
          </div>
        </div>
      </Link>
    </div>
  );
};

// Companies Management
const CompaniesManagement = ({ token }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await fetch(`${API}/admin/companies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCompanies(data);
      }
    } catch (error) {
      toast.error('فشل تحميل الشركات');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الشركة؟')) return;
    
    try {
      const response = await fetch(`${API}/admin/companies/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('تم حذف الشركة');
        fetchCompanies();
      }
    } catch (error) {
      toast.error('فشل حذف الشركة');
    }
  };

  const handleActivate = async (id, days = 365) => {
    try {
      const response = await fetch(`${API}/admin/companies/${id}/activate?duration_days=${days}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('تم تفعيل الشركة');
        fetchCompanies();
      }
    } catch (error) {
      toast.error('فشل تفعيل الشركة');
    }
  };

  const handleRegenerateLicense = async (id) => {
    try {
      const response = await fetch(`${API}/admin/companies/${id}/regenerate-license`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`مفتاح جديد: ${data.license_key}`);
        fetchCompanies();
      }
    } catch (error) {
      toast.error('فشل إنشاء مفتاح جديد');
    }
  };

  const filteredCompanies = companies.filter(c => 
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.subdomain.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="بحث عن شركة..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pr-10 pl-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
          />
        </div>
        <button
          onClick={() => { setEditingCompany(null); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          إضافة شركة
        </button>
      </div>

      {/* Companies List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : filteredCompanies.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
          <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">لا توجد شركات</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredCompanies.map((company) => (
            <CompanyCard
              key={company.id}
              company={company}
              token={token}
              onEdit={() => { setEditingCompany(company); setShowModal(true); }}
              onDelete={() => handleDelete(company.id)}
              onActivate={() => handleActivate(company.id)}
              onRegenerateLicense={() => handleRegenerateLicense(company.id)}
              onRefresh={fetchCompanies}
            />
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <CompanyModal
          company={editingCompany}
          token={token}
          onClose={() => { setShowModal(false); setEditingCompany(null); }}
          onSuccess={() => { setShowModal(false); setEditingCompany(null); fetchCompanies(); }}
        />
      )}
    </div>
  );
};

// Company Card
const CompanyCard = ({ company, onEdit, onDelete, onActivate, onRegenerateLicense, token, onRefresh }) => {
  const [showLicense, setShowLicense] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showDbModal, setShowDbModal] = useState(false);
  const [dbStats, setDbStats] = useState(null);
  const [loadingAction, setLoadingAction] = useState(null);

  const copyLicense = () => {
    navigator.clipboard.writeText(company.license_key);
    toast.success('تم نسخ مفتاح الترخيص');
  };

  // Fetch company database stats
  const fetchDbStats = async () => {
    try {
      const response = await fetch(`${API}/admin/company/${company.id}/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDbStats(data);
      }
    } catch (error) {
      toast.error('فشل تحميل الإحصائيات');
    }
  };

  // Export company data
  const handleExport = async () => {
    setLoadingAction('export');
    try {
      const response = await fetch(`${API}/admin/company/${company.id}/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Download as JSON file
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${company.subdomain}_backup_${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`تم تصدير ${data.total_documents} وثيقة`);
      } else {
        toast.error('فشل التصدير');
      }
    } catch (error) {
      toast.error('فشل التصدير');
    } finally {
      setLoadingAction(null);
    }
  };

  // Import company data
  const handleImport = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      setLoadingAction('import');
      try {
        const text = await file.text();
        const importData = JSON.parse(text);
        
        const response = await fetch(`${API}/admin/company/${company.id}/import`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(importData)
        });
        
        if (response.ok) {
          const result = await response.json();
          toast.success(result.message);
          fetchDbStats();
        } else {
          const error = await response.json();
          toast.error(error.detail || 'فشل الاستيراد');
        }
      } catch (error) {
        toast.error('ملف غير صالح');
      } finally {
        setLoadingAction(null);
      }
    };
    input.click();
  };

  // Clear company data
  const handleClear = async () => {
    if (!window.confirm(`⚠️ تحذير!\n\nسيتم حذف جميع بيانات "${company.name}" نهائياً.\n\nهل أنت متأكد؟`)) return;
    if (!window.confirm('هذا الإجراء لا يمكن التراجع عنه! هل تريد المتابعة؟')) return;
    
    setLoadingAction('clear');
    try {
      const response = await fetch(`${API}/admin/company/${company.id}/clear?confirm=true`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        fetchDbStats();
      } else {
        toast.error('فشل التنظيف');
      }
    } catch (error) {
      toast.error('فشل التنظيف');
    } finally {
      setLoadingAction(null);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        {/* Info */}
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-semibold text-gray-800">{company.name}</h3>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${company.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {company.is_active ? 'نشط' : 'معطل'}
            </span>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <span className="text-gray-400">النطاق:</span>
              <code className="bg-gray-100 px-2 py-0.5 rounded text-blue-600">{company.subdomain}</code>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-gray-400" />
              {company.email}
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-400" />
              {company.current_users} / {company.max_users} مستخدم
            </div>
            {company.expires_at && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                ينتهي: {new Date(company.expires_at).toLocaleDateString('ar-SA')}
              </div>
            )}
          </div>

          {/* License Key */}
          <div className="mt-3 flex items-center gap-2">
            <Key className="w-4 h-4 text-gray-400" />
            <code className="bg-gray-100 px-2 py-1 rounded text-sm">
              {showLicense ? company.license_key : '••••-••••-••••-••••'}
            </code>
            <button onClick={() => setShowLicense(!showLicense)} className="p-1 hover:bg-gray-100 rounded">
              {showLicense ? <EyeOff className="w-4 h-4 text-gray-500" /> : <Eye className="w-4 h-4 text-gray-500" />}
            </button>
            <button onClick={copyLicense} className="p-1 hover:bg-gray-100 rounded">
              <Copy className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          {/* Database Info */}
          <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
            <Database className="w-4 h-4" />
            <span>قاعدة البيانات:</span>
            <code className="bg-purple-50 text-purple-600 px-2 py-0.5 rounded text-xs">{company.database_name || 'غير محدد'}</code>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Database Management Buttons */}
          <button
            onClick={handleExport}
            disabled={loadingAction === 'export'}
            className="flex items-center gap-1 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm disabled:opacity-50"
            title="تصدير البيانات"
          >
            {loadingAction === 'export' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            تصدير
          </button>
          <button
            onClick={handleImport}
            disabled={loadingAction === 'import'}
            className="flex items-center gap-1 px-3 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors text-sm disabled:opacity-50"
            title="استيراد البيانات"
          >
            {loadingAction === 'import' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            استيراد
          </button>
          <button
            onClick={handleClear}
            disabled={loadingAction === 'clear'}
            className="flex items-center gap-1 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm disabled:opacity-50"
            title="تنظيف البيانات"
          >
            {loadingAction === 'clear' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            تنظيف
          </button>
          
          <div className="w-px h-8 bg-gray-200 mx-1"></div>
          
          <button
            onClick={onActivate}
            className="p-2 hover:bg-green-50 text-green-600 rounded-lg transition-colors"
            title="تفعيل / تجديد"
          >
            <Power className="w-5 h-5" />
          </button>
          <button
            onClick={onRegenerateLicense}
            className="p-2 hover:bg-amber-50 text-amber-600 rounded-lg transition-colors"
            title="إنشاء مفتاح جديد"
          >
            <Key className="w-5 h-5" />
          </button>
          <button
            onClick={onEdit}
            className="p-2 hover:bg-blue-50 text-blue-600 rounded-lg transition-colors"
            title="تعديل"
          >
            <Edit className="w-5 h-5" />
          </button>
          <button
            onClick={onDelete}
            className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
            title="حذف"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Company Modal
const CompanyModal = ({ company, token, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: company?.name || '',
    subdomain: company?.subdomain || '',
    email: company?.email || '',
    phone: company?.phone || '',
    address: company?.address || '',
    max_users: company?.max_users || 10
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = company 
        ? `${API}/admin/companies/${company.id}`
        : `${API}/admin/companies`;
      
      const response = await fetch(url, {
        method: company ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(form)
      });

      if (response.ok) {
        toast.success(company ? 'تم تحديث الشركة' : 'تم إضافة الشركة');
        onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'حدث خطأ');
      }
    } catch (error) {
      toast.error('حدث خطأ');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-800">
            {company ? 'تعديل الشركة' : 'إضافة شركة جديدة'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">اسم الشركة *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({...form, name: e.target.value})}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">النطاق الفرعي * (بالإنجليزية فقط)</label>
            <input
              type="text"
              value={form.subdomain}
              onChange={(e) => setForm({...form, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
              placeholder="company-name"
              required
              disabled={!!company}
            />
            <p className="text-xs text-gray-500 mt-1">سيكون الرابط: {form.subdomain || 'company'}.yourapp.com</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">البريد الإلكتروني *</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({...form, email: e.target.value})}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">رقم الهاتف</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({...form, phone: e.target.value})}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">العنوان</label>
            <input
              type="text"
              value={form.address}
              onChange={(e) => setForm({...form, address: e.target.value})}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">الحد الأقصى للمستخدمين</label>
            <input
              type="number"
              value={form.max_users}
              onChange={(e) => setForm({...form, max_users: parseInt(e.target.value) || 10})}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
              min="1"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {company ? 'تحديث' : 'إضافة'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Users Management
const UsersManagement = ({ token, user, isSuperAdmin }) => {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [search, setSearch] = useState('');
  const [filterCompany, setFilterCompany] = useState('');

  useEffect(() => {
    fetchUsers();
    if (isSuperAdmin) {
      fetchCompanies();
    }
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      toast.error('فشل تحميل المستخدمين');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await fetch(`${API}/admin/companies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCompanies(data);
      }
    } catch (error) {
      console.error('Failed to fetch companies');
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/toggle-active`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('تم تحديث حالة المستخدم');
        fetchUsers();
      }
    } catch (error) {
      toast.error('فشل تحديث الحالة');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المستخدم؟')) return;
    
    try {
      const response = await fetch(`${API}/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('تم حذف المستخدم');
        fetchUsers();
      }
    } catch (error) {
      toast.error('فشل حذف المستخدم');
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase());
    const matchesCompany = !filterCompany || u.company_id === filterCompany;
    return matchesSearch && matchesCompany;
  });

  const getRoleBadge = (role) => {
    switch (role) {
      case 'super_admin':
        return <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">مدير النظام</span>;
      case 'company_admin':
        return <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">مدير شركة</span>;
      default:
        return <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">موظف</span>;
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="بحث عن مستخدم..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pr-10 pl-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
            />
          </div>
          {isSuperAdmin && companies.length > 0 && (
            <select
              value={filterCompany}
              onChange={(e) => setFilterCompany(e.target.value)}
              className="px-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
            >
              <option value="">كل الشركات</option>
              {companies.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
        >
          <UserPlus className="w-5 h-5" />
          إضافة مستخدم
        </button>
      </div>

      {/* Users List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">لا يوجد مستخدمين</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">الاسم</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">البريد</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">الدور</th>
                  {isSuperAdmin && <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">الشركة</th>}
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">الحالة</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">إجراءات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredUsers.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white text-sm font-medium">
                          {u.name?.charAt(0) || 'U'}
                        </div>
                        <span className="font-medium text-gray-800">{u.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{u.email}</td>
                    <td className="px-4 py-3">{getRoleBadge(u.role)}</td>
                    {isSuperAdmin && <td className="px-4 py-3 text-gray-600">{u.company_name || '-'}</td>}
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {u.is_active ? 'نشط' : 'معطل'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {u.id !== user.id && (
                          <>
                            {u.role === 'employee' && (
                              <button
                                onClick={() => { setSelectedUser(u); setShowPermissionsModal(true); }}
                                className="p-1.5 hover:bg-purple-50 text-purple-600 rounded-lg transition-colors"
                                title="الصلاحيات"
                              >
                                <Shield className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleToggleActive(u.id)}
                              className={`p-1.5 rounded-lg transition-colors ${u.is_active ? 'hover:bg-amber-50 text-amber-600' : 'hover:bg-green-50 text-green-600'}`}
                              title={u.is_active ? 'تعطيل' : 'تفعيل'}
                            >
                              <Power className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(u.id)}
                              className="p-1.5 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
                              title="حذف"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showModal && (
        <AddUserModal
          token={token}
          companies={companies}
          currentUser={user}
          isSuperAdmin={isSuperAdmin}
          onClose={() => setShowModal(false)}
          onSuccess={() => { setShowModal(false); fetchUsers(); }}
        />
      )}

      {/* Permissions Modal */}
      {showPermissionsModal && selectedUser && (
        <PermissionsModal
          user={selectedUser}
          token={token}
          onClose={() => { setShowPermissionsModal(false); setSelectedUser(null); }}
          onSuccess={() => { setShowPermissionsModal(false); setSelectedUser(null); fetchUsers(); }}
        />
      )}
    </div>
  );
};

// Permissions Modal
const PermissionsModal = ({ user, token, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [expandedSystems, setExpandedSystems] = useState(['procurement']); // نظام المشتريات مفتوح افتراضياً
  const [allProjects, setAllProjects] = useState([]);
  const [assignedProjects, setAssignedProjects] = useState(user.assigned_projects || []);
  const [projectSearch, setProjectSearch] = useState('');
  const [projectsLoading, setProjectsLoading] = useState(false);

  // تحميل قائمة المشاريع المتاحة لتخصيصها
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setProjectsLoading(true);
      try {
        const res = await fetch(`${API}/procurement/projects`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok && !cancelled) {
          const data = await res.json();
          setAllProjects(Array.isArray(data) ? data : []);
        }
      } finally {
        if (!cancelled) setProjectsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const filteredProjects = allProjects.filter(p => {
    const q = projectSearch.trim().toLowerCase();
    if (!q) return true;
    return (p.project_number || '').toLowerCase().includes(q)
      || (p.project_name || '').toLowerCase().includes(q);
  });

  const toggleProject = (pid) => {
    setAssignedProjects(prev => prev.includes(pid) ? prev.filter(x => x !== pid) : [...prev, pid]);
  };
  const selectAllProjects = () => setAssignedProjects(allProjects.map(p => p.id));
  const clearAllProjects = () => setAssignedProjects([]);
  
  // الصلاحيات الرئيسية
  const mainPermissions = ['buildings', 'analysis', 'compare', 'procurement', 'balance', 'system'];
  
  // الصلاحيات الفرعية لنظام المشتريات
  const procurementSubPermissions = [
    'proc_categories',    // التصنيفات الرئيسية
    'proc_items',         // إدارة الأصناف
    'proc_suppliers',     // إدارة الموردين
    'proc_projects',      // إدارة المشاريع
    'proc_quotes',        // عروض الأسعار
    'proc_comparison',    // تقرير المقارنة
    'proc_tags',          // التصنيفات
    'proc_invoices',      // تسليم الفواتير
    'proc_receipts',      // إدارة الاستلامات
    'proc_invoice_matching', // مطابقة فواتير الموردين
    'proc_receipt_actions',  // إجراءات الاستلامات المتأخرة (إنشاء/تعديل)
    'receipt_approve',    // صلاحية اعتماد كشوف الاستلام
  ];
  
  // جميع الصلاحيات الفردية
  const allIndividualPerms = [...mainPermissions.filter(p => p !== 'procurement'), ...procurementSubPermissions];
  
  // Initialize permissions
  const initializePermissions = () => {
    const userPerms = user.permissions || [];
    if (userPerms.includes('all')) {
      return [...mainPermissions, ...procurementSubPermissions];
    }
    // إذا كان لديه صلاحية procurement الكاملة، أضف كل الفرعية
    if (userPerms.includes('procurement') && !procurementSubPermissions.some(p => userPerms.includes(p))) {
      return [...userPerms, ...procurementSubPermissions];
    }
    return userPerms.filter(p => p !== 'all');
  };
  
  const [permissions, setPermissions] = useState(initializePermissions);

  const allPermissionsList = [
    { id: 'all', name: 'جميع الصلاحيات', description: 'الوصول لكل أقسام النظام', icon: '🔓', type: 'main' },
    { id: 'buildings', name: 'كميات العمائر', description: 'إدارة كميات العمائر السكنية', icon: '🏢', type: 'main' },
    { id: 'analysis', name: 'تحليل الأسعار', description: 'تحليل فروقات الأسعار', icon: '📊', type: 'main' },
    { id: 'compare', name: 'مقارنة المشاريع', description: 'مقارنة ملفات Excel', icon: '📈', type: 'main' },
    { id: 'procurement', name: 'نظام المشتريات', description: 'إدارة عروض الأسعار والموردين', icon: '🛒', type: 'main', hasSubPermissions: true },
    { id: 'balance', name: 'ميزان المراجعة', description: 'مقارنة موازين المراجعة', icon: '⚖️', type: 'main' },
    { id: 'system', name: 'إعدادات النظام', description: 'النسخ الاحتياطي والصيانة', icon: '⚙️', type: 'main' },
  ];
  
  const procurementSubPermissionsList = [
    { id: 'proc_categories', name: 'التصنيفات الرئيسية', icon: '📁' },
    { id: 'proc_items', name: 'إدارة الأصناف', icon: '📦' },
    { id: 'proc_suppliers', name: 'إدارة الموردين', icon: '👥' },
    { id: 'proc_projects', name: 'إدارة المشاريع', icon: '🏗️' },
    { id: 'proc_quotes', name: 'عروض الأسعار', icon: '📋' },
    { id: 'proc_comparison', name: 'تقرير المقارنة', icon: '📊' },
    { id: 'proc_tags', name: 'التصنيفات', icon: '🏷️' },
    { id: 'proc_invoices', name: 'تسليم الفواتير', icon: '🧾' },
    { id: 'proc_receipts', name: 'إدارة الاستلامات', icon: '📝' },
    { id: 'proc_invoice_matching', name: 'مطابقة فواتير الموردين', icon: '🔗' },
    { id: 'proc_receipt_actions', name: 'إجراءات الاستلامات المتأخرة', icon: '⚠️', isAction: true, hint: 'يسمح للمستخدم بإنشاء/تعديل إجراءات الاستلامات بدون فواتير (يتطلب أيضاً صلاحية "مطابقة فواتير الموردين")' },
    { id: 'receipt_approve', name: 'صلاحية اعتماد الاستلامات', icon: '✅', isAction: true, hint: 'يسمح للمستخدم باعتماد/رفض كشوف الاستلام (يتطلب أيضاً صلاحية "إدارة الاستلامات")' },
  ];

  // Check if all permissions are selected
  const isAllSelected = mainPermissions.every(p => permissions.includes(p)) && 
                        procurementSubPermissions.every(p => permissions.includes(p));
  
  // Check if all procurement sub-permissions are selected
  const isAllProcurementSelected = procurementSubPermissions.every(p => permissions.includes(p));
  const hasSomeProcurementSelected = procurementSubPermissions.some(p => permissions.includes(p));

  const togglePermission = (permId) => {
    if (permId === 'all') {
      // Toggle all permissions
      if (isAllSelected) {
        setPermissions([]);
      } else {
        setPermissions([...mainPermissions, ...procurementSubPermissions]);
      }
    } else if (permId === 'procurement') {
      // Toggle all procurement sub-permissions
      if (isAllProcurementSelected) {
        setPermissions(permissions.filter(p => !procurementSubPermissions.includes(p) && p !== 'procurement'));
      } else {
        const newPerms = permissions.filter(p => !procurementSubPermissions.includes(p));
        setPermissions([...newPerms, 'procurement', ...procurementSubPermissions]);
      }
    } else if (procurementSubPermissions.includes(permId)) {
      // Toggle individual procurement sub-permission
      if (permissions.includes(permId)) {
        const newPerms = permissions.filter(p => p !== permId);
        // إذا لم يتبق أي صلاحية فرعية، أزل صلاحية المشتريات الرئيسية
        if (!procurementSubPermissions.some(p => newPerms.includes(p))) {
          setPermissions(newPerms.filter(p => p !== 'procurement'));
        } else {
          setPermissions(newPerms);
        }
      } else {
        // أضف الصلاحية الفرعية وصلاحية المشتريات الرئيسية
        const newPerms = [...permissions, permId];
        if (!newPerms.includes('procurement')) {
          newPerms.push('procurement');
        }
        setPermissions(newPerms);
      }
    } else {
      // Toggle other individual permission
      if (permissions.includes(permId)) {
        setPermissions(permissions.filter(p => p !== permId));
      } else {
        setPermissions([...permissions, permId]);
      }
    }
  };
  
  const toggleExpanded = (systemId) => {
    if (expandedSystems.includes(systemId)) {
      setExpandedSystems(expandedSystems.filter(s => s !== systemId));
    } else {
      setExpandedSystems([...expandedSystems, systemId]);
    }
  };
  
  // Convert permissions for submission
  const getPermissionsForSubmit = () => {
    if (isAllSelected) {
      return ['all'];
    }
    return permissions;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (permissions.length === 0) {
      toast.error('يجب اختيار صلاحية واحدة على الأقل');
      return;
    }
    
    setLoading(true);
    
    const permissionsToSubmit = getPermissionsForSubmit();

    try {
      const response = await fetch(`${API}/admin/users/${user.id}/permissions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(permissionsToSubmit)
      });

      if (response.ok) {
        // 2) حفظ المشاريع المخصَّصة (موازياً للصلاحيات)
        try {
          await fetch(`${API}/admin/users/${user.id}/projects`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(assignedProjects)
          });
        } catch {
          // لا تُفشل العملية كلياً لو فشل تحديث المشاريع
        }
        toast.success('تم تحديث الصلاحيات والمشاريع');
        onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'حدث خطأ');
      }
    } catch (error) {
      toast.error('حدث خطأ');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-800">صلاحيات المستخدم</h2>
          <p className="text-sm text-gray-500 mt-1">{user.name}</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-3">
          {allPermissionsList.map((perm) => {
            const isChecked = perm.id === 'all' 
              ? isAllSelected 
              : perm.id === 'procurement'
                ? hasSomeProcurementSelected
                : permissions.includes(perm.id);
            const isExpanded = expandedSystems.includes(perm.id);
            const isIndeterminate = perm.id === 'procurement' && hasSomeProcurementSelected && !isAllProcurementSelected;
            
            return (
              <div key={perm.id}>
                <div
                  className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    isChecked
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div 
                    onClick={() => togglePermission(perm.id)}
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all shrink-0 ${
                      isChecked
                        ? isIndeterminate ? 'bg-blue-400 border-blue-400' : 'bg-blue-600 border-blue-600'
                        : 'border-gray-300'
                    }`}
                  >
                    {isChecked && (
                      isIndeterminate ? (
                        <div className="w-2 h-0.5 bg-white rounded" />
                      ) : (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )
                    )}
                  </div>
                  <div className="flex-1" onClick={() => togglePermission(perm.id)}>
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{perm.icon}</span>
                      <span className="font-medium text-gray-800">{perm.name}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{perm.description}</p>
                  </div>
                  {perm.hasSubPermissions && (
                    <button
                      type="button"
                      onClick={() => toggleExpanded(perm.id)}
                      className="p-1 hover:bg-blue-100 rounded-lg transition-colors"
                    >
                      <svg 
                        className={`w-5 h-5 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  )}
                </div>
                
                {/* Sub-permissions for procurement */}
                {perm.id === 'procurement' && isExpanded && (
                  <div className="mr-6 mt-2 space-y-2 border-r-2 border-blue-200 pr-4">
                    {procurementSubPermissionsList.map((subPerm) => {
                      const isSubChecked = permissions.includes(subPerm.id);
                      return (
                        <div key={subPerm.id}>
                          {subPerm.isAction && (
                            <div className="mt-3 mb-1 text-[11px] font-semibold text-amber-700 uppercase tracking-wide">
                              ⚡ صلاحيات الإجراءات
                            </div>
                          )}
                          <label
                            onClick={() => togglePermission(subPerm.id)}
                            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                              isSubChecked
                                ? (subPerm.isAction ? 'border-amber-400 bg-amber-50' : 'border-blue-400 bg-blue-50')
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className={`w-4 h-4 mt-0.5 rounded border-2 flex items-center justify-center transition-all shrink-0 ${
                              isSubChecked
                                ? (subPerm.isAction ? 'bg-amber-600 border-amber-600' : 'bg-blue-600 border-blue-600')
                                : 'border-gray-300'
                            }`}>
                              {isSubChecked && (
                                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                            <span className="text-base">{subPerm.icon}</span>
                            <div className="flex-1">
                              <span className="text-sm text-gray-700">{subPerm.name}</span>
                              {subPerm.hint && (
                                <p className="text-[11px] text-gray-500 mt-0.5">{subPerm.hint}</p>
                              )}
                            </div>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* ============ المشاريع المخصَّصة للمستخدم ============ */}
          <div className="border-2 border-emerald-200 rounded-xl bg-emerald-50/40 p-4 mt-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-bold text-emerald-900 text-sm flex items-center gap-2">
                  <span className="text-base">🏗️</span> المشاريع المخصَّصة
                </h3>
                <p className="text-[11px] text-emerald-700/80">يظهر فقط هذه المشاريع للمستخدم عند إضافة استلام جديد</p>
              </div>
              <span className="bg-emerald-600 text-white text-xs font-bold px-2 py-1 rounded-full" data-testid="assigned-projects-count">
                {assignedProjects.length}
              </span>
            </div>
            {projectsLoading ? (
              <div className="text-center py-3 text-xs text-gray-500">جاري التحميل...</div>
            ) : allProjects.length === 0 ? (
              <div className="text-center py-3 text-xs text-gray-500">
                لا توجد مشاريع في النظام بعد. أضف مشاريع من صفحة «إدارة المشاريع».
              </div>
            ) : (
              <>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={projectSearch}
                    onChange={(e) => setProjectSearch(e.target.value)}
                    placeholder="ابحث برقم/اسم المشروع..."
                    data-testid="projects-search-input"
                    className="flex-1 h-9 px-3 text-xs border border-emerald-200 rounded-lg focus:border-emerald-500 outline-none bg-white"
                  />
                  <button
                    type="button"
                    onClick={selectAllProjects}
                    data-testid="select-all-projects-btn"
                    className="text-[11px] px-2.5 py-1 rounded-lg bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                  >
                    تحديد الكل
                  </button>
                  <button
                    type="button"
                    onClick={clearAllProjects}
                    data-testid="clear-all-projects-btn"
                    className="text-[11px] px-2.5 py-1 rounded-lg bg-white border border-gray-300 text-gray-600 hover:bg-gray-100"
                  >
                    مسح
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto space-y-1 bg-white rounded-lg border border-emerald-200 p-1.5">
                  {filteredProjects.length === 0 ? (
                    <div className="text-center py-3 text-xs text-gray-400">لا توجد نتائج</div>
                  ) : filteredProjects.map((p) => {
                    const checked = assignedProjects.includes(p.id);
                    return (
                      <label
                        key={p.id}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-emerald-50 ${checked ? 'bg-emerald-50' : ''}`}
                        data-testid={`project-row-${p.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleProject(p.id)}
                          className="w-4 h-4 accent-emerald-600"
                        />
                        <span className="text-xs font-bold text-emerald-700 shrink-0">#{p.project_number || '—'}</span>
                        <span className="text-xs text-gray-700 truncate">{p.project_name || ''}</span>
                      </label>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              حفظ الصلاحيات
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Add User Modal
const AddUserModal = ({ token, companies, currentUser, isSuperAdmin, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  
  const allIndividualPerms = ['buildings', 'analysis', 'compare', 'procurement', 'balance', 'system'];
  
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'employee',
    company_id: isSuperAdmin ? '' : currentUser?.company_id || '',
    permissions: [...allIndividualPerms] // Start with all permissions
  });

  const allPermissions = [
    { id: 'all', name: 'جميع الصلاحيات' },
    { id: 'buildings', name: 'كميات العمائر' },
    { id: 'analysis', name: 'تحليل الأسعار' },
    { id: 'compare', name: 'مقارنة المشاريع' },
    { id: 'procurement', name: 'نظام المشتريات' },
    { id: 'balance', name: 'ميزان المراجعة' },
    { id: 'system', name: 'إعدادات النظام' },
  ];

  // Check if all individual permissions are selected
  const isAllSelected = allIndividualPerms.every(p => form.permissions.includes(p));

  const togglePermission = (permId) => {
    if (permId === 'all') {
      // Toggle all permissions
      if (isAllSelected) {
        setForm({...form, permissions: []});
      } else {
        setForm({...form, permissions: [...allIndividualPerms]});
      }
    } else {
      // Toggle individual permission
      if (form.permissions.includes(permId)) {
        setForm({...form, permissions: form.permissions.filter(p => p !== permId)});
      } else {
        setForm({...form, permissions: [...form.permissions, permId]});
      }
    }
  };
  
  // Convert permissions for submission - use 'all' if everything selected
  const getPermissionsForSubmit = () => {
    if (isAllSelected) {
      return ['all'];
    }
    return form.permissions;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (form.role === 'employee' && form.permissions.length === 0) {
      toast.error('يجب اختيار صلاحية واحدة على الأقل');
      return;
    }
    
    setLoading(true);
    
    const submitData = {
      ...form,
      permissions: form.role === 'employee' ? getPermissionsForSubmit() : ['all']
    };

    try {
      const response = await fetch(`${API}/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        toast.success('تم إضافة المستخدم');
        onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'حدث خطأ');
      }
    } catch (error) {
      toast.error('حدث خطأ');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-800">إضافة مستخدم جديد</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">الاسم *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({...form, name: e.target.value})}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">البريد الإلكتروني *</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({...form, email: e.target.value})}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">كلمة المرور *</label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({...form, password: e.target.value})}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
              required
              minLength={6}
            />
          </div>

          {isSuperAdmin && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الدور *</label>
                <select
                  value={form.role}
                  onChange={(e) => setForm({...form, role: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                >
                  <option value="employee">موظف</option>
                  <option value="company_admin">مدير شركة</option>
                  <option value="super_admin">مدير النظام</option>
                </select>
              </div>

              {form.role !== 'super_admin' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">الشركة</label>
                  <select
                    value={form.company_id}
                    onChange={(e) => setForm({...form, company_id: e.target.value})}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                  >
                    <option value="">بدون شركة</option>
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}

          {/* Permissions for employees */}
          {form.role === 'employee' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">الصلاحيات</label>
              <div className="space-y-2 max-h-48 overflow-y-auto border rounded-xl p-2">
                {allPermissions.map((perm) => {
                  const isChecked = perm.id === 'all' ? isAllSelected : form.permissions.includes(perm.id);
                  return (
                    <div
                      key={perm.id}
                      onClick={() => togglePermission(perm.id)}
                      className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${
                        isChecked
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
                        isChecked
                          ? 'bg-blue-600 border-blue-600'
                          : 'border-gray-300'
                      }`}>
                        {isChecked && (
                          <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <span className="text-sm text-gray-700">{perm.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              إضافة
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminDashboard;
